/**
 * IncorporationBanner
 * Shown in the lease wizard when an accepted application or active listing
 * context exists for the unit. Lets the landlord pre-fill the wizard from
 * application/listing data.
 */

import { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Button } from '../ui/button';
import { getMoveIn } from '../../lib/move-in.api';
import { formatCalendarDate } from '../../utils/date';
import { asCalendarDateString } from '@resida/shared';
import type { MoveInProjection, MoveInSuggestion } from '../../types/move-in';

interface IncorporationBannerProps {
  unitId: string;
  onDismiss: () => void;
}

// Suggestions from listing source (Source B) have a contributor that doesn't start with "applicant."
const LISTING_CONTRIBUTOR_PREFIX = 'listing.';
const APPLICATION_CONTRIBUTOR_PREFIXES = ['applicant.'];

function hasApplicationSuggestions(suggestions: MoveInSuggestion[]): boolean {
  return suggestions.some((s) =>
    APPLICATION_CONTRIBUTOR_PREFIXES.some((p) => s.contributor.startsWith(p))
  );
}

function hasListingSuggestions(suggestions: MoveInSuggestion[]): boolean {
  return suggestions.some((s) => s.contributor.startsWith(LISTING_CONTRIBUTOR_PREFIX));
}

function getActiveSuggestions(suggestions: MoveInSuggestion[]): MoveInSuggestion[] {
  return suggestions.filter((s) => s.preference === null);
}

// camelCase/snake_case → "spaced lowercase words"
function humanize(s: string): string {
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
}

function formatFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    'inputs.rentAmount': 'Rent amount',
    'inputs.startDate': 'Move-in date',
    'inputs.securityDeposit': 'Security deposit',
    'inputs.endDate': 'Lease end date',
    'extraVariables.smoking_prohibited': 'Smoking policy',
    'extraVariables.parking_monthly_fee': 'Parking fee',
    'extraVariables.parking_included': 'Parking included',
  };
  if (labels[field]) return labels[field];

  // 'tenants[0].firstName' → 'Tenant 1 first name' (applicantIdentityContributor emits
  // one suggestion per tenant field — these never had a static label above).
  const tenantMatch = field.match(/^tenants\[(\d+)\]\.(.+)$/);
  if (tenantMatch?.[1] != null && tenantMatch[2] != null) {
    const tenantNumber = Number(tenantMatch[1]) + 1;
    return `Tenant ${tenantNumber} ${humanize(tenantMatch[2])}`;
  }
  if (field === 'coSigners') return 'Co-signer';

  const stripped = field.replace(/^(inputs|extraVariables|pets)\./, '');
  const label = humanize(stripped);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatFieldValue(field: string, value: unknown): string {
  // smoking_prohibited is an inverted flag (true = NOT allowed) — the generic boolean
  // Yes/No below would render "Smoking policy: Yes" for a no-smoking listing.
  if (field === 'extraVariables.smoking_prohibited' && typeof value === 'boolean') {
    return value ? 'Not allowed' : 'Allowed';
  }
  if (field.includes('Amount') || field.includes('Deposit') || field.toLowerCase().includes('fee')) {
    if (typeof value === 'number') {
      // Contributors already emit dollar amounts (cents were converted upstream) — do NOT
      // divide by 100 again. Only recurring charges get the "/mo" suffix (a deposit is one-time).
      const dollars = `$${value.toLocaleString()}`;
      return field.includes('Deposit') ? dollars : `${dollars}/mo`;
    }
  }
  if (field.includes('Date') && typeof value === 'string') {
    return formatCalendarDate(asCalendarDateString(value));
  }
  if (field === 'coSigners' && Array.isArray(value)) {
    return value.map((c) => (c as { name?: string }).name).filter(Boolean).join(', ') || '—';
  }
  if (field === 'pets.animals' && Array.isArray(value)) {
    return value.map((p) => (p as { name?: string }).name || 'Unnamed').join(', ') || '—';
  }
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ') || '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value != null ? String(value) : '—';
}

export function IncorporationBanner({ unitId, onDismiss }: IncorporationBannerProps) {
  const [projection, setProjection] = useState<MoveInProjection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMoveIn(unitId)
      .then((data) => { if (!cancelled) setProjection(data); })
      .catch(() => { /* Non-fatal: silently hide banner */ })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [unitId]);

  // Don't render until we know the answer
  if (isLoading || !projection) return null;

  const activeSuggestions = getActiveSuggestions(projection.suggestions);
  const hasLink = projection.link !== null;
  const hasAppSuggestions = hasApplicationSuggestions(activeSuggestions);
  const hasListSuggestions = hasListingSuggestions(activeSuggestions);

  // Only show banner if there's a link or listing-sourced suggestions
  if (!hasLink && !hasListSuggestions && !hasAppSuggestions) return null;

  const title = hasLink
    ? 'Pre-filled from the accepted application'
    : 'Pre-filled from the active listing';

  return (
    <div
      data-testid="incorporation-banner"
      className="mb-4 border border-primary/30 bg-primary/5 rounded-lg p-4"
      role="region"
      aria-label="Pre-fill suggestion"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <Sparkles
            className="h-4 w-4 text-primary mt-0.5 shrink-0"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1">{title}</h3>
            <p className="text-xs text-muted-foreground mb-3">
              We pre-filled the lease with the values below. Review them as you go — anything you
              already filled in was left untouched, and nothing is final until you generate.
            </p>

            {activeSuggestions.length > 0 && (
              <ul className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                {activeSuggestions.map((s) => (
                  <li key={s.id} className="text-xs text-foreground">
                    <span className="font-medium">{formatFieldLabel(s.field)}</span>
                    <span className="text-muted-foreground ml-1">
                      {formatFieldValue(s.field, s.proposedValue)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-center gap-2 mt-2">
              <Button
                variant="ghost"
                size="default"
                onClick={onDismiss}
                data-testid="incorporation-banner-dismiss-btn"
                aria-label="Dismiss pre-fill note"
                className="text-xs min-h-[44px]"
              >
                Got it
              </Button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Close pre-fill banner"
          data-testid="incorporation-banner-close-btn"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
